/**
 * TanStack Query hooks for data-quality suggestion generation.
 */

import { useQuery } from "@tanstack/react-query"
import type { DtaSuggestionsResponse } from "@/generated/sdk"
import type { DTADetails } from "@/services/dtaApi"
import { dtaApiService } from "@/services/dtaApi"

const dataQualityKeys = {
  all: ["dataQuality"] as const,
  suggestions: (dtaId: string | undefined) =>
    [...dataQualityKeys.all, "suggestions", dtaId ?? null] as const,
  translation: (dtaId: string | undefined) =>
    [...dataQualityKeys.all, "translation", dtaId ?? null] as const,
}

/**
 * Maximum number of poll attempts before giving up on suggestion generation. At 5s per poll this is
 * ~2 minutes (Bedrock generation typically takes 60-90s).
 */
export const MAX_SUGGESTION_POLLS = 24

/**
 * Poll cadence for DQ suggestions: 5s while the backend is still generating and we're under the
 * attempt cap, otherwise stop (false). Owning both the terminal-status check and the cap here means
 * the timeout actually halts the network poll, not just the UI label.
 */
export function suggestionsInterval(
  data: Pick<DtaSuggestionsResponse, "status"> | undefined,
  attempts: number,
): number | false {
  const stillGenerating = !data || data.status === "generating"
  return stillGenerating && attempts < MAX_SUGGESTION_POLLS ? 5000 : false
}

/**
 * Fetches AI-generated data-quality rule suggestions for a DTA, polling every 5s while the backend
 * generates them and stopping once the status is terminal (ready/unavailable/error) or the attempt
 * cap is hit.
 */
export function useDataQualitySuggestions(dtaId: string | undefined, enabled = true) {
  return useQuery({
    queryKey: dataQualityKeys.suggestions(dtaId),
    queryFn: () => dtaApiService.getDataQualitySuggestions(dtaId as string),
    enabled: !!dtaId && enabled,
    refetchInterval: (query) => suggestionsInterval(query.state.data, query.state.dataUpdateCount),
  })
}

/**
 * Whether a DTA still has pending EDC rule translations (the backend clears `_edc_source_rules`
 * from document_content once translation completes).
 */
export function hasPendingTranslation(dta: DTADetails | undefined): boolean {
  const rules = dta?.document_content?._edc_source_rules
  return Array.isArray(rules) && rules.length > 0
}

/**
 * Polls a DTA's detail while SDS rule translation is pending, stopping (refetchInterval false) once
 * `_edc_source_rules` clears. Uses a dedicated cache key so it doesn't disturb the page's primary
 * DTA-detail query. `enabled` gates polling to only run while the caller knows translation is
 * pending.
 */
export function useTranslationStatus(dtaId: string | undefined, enabled: boolean) {
  return useQuery({
    queryKey: dataQualityKeys.translation(dtaId),
    queryFn: () => dtaApiService.getDTADetails(dtaId as string),
    enabled: !!dtaId && enabled,
    refetchInterval: (query) => (hasPendingTranslation(query.state.data) ? 5000 : false),
  })
}
